import { ApprovalsFilter } from '@/features/approvals/types/ApprovalsFilter';
import { ApprovalsTab } from '@/features/approvals/types/ApprovalsTab';

const ApprovalsTabToKey: Record<ApprovalsTab, string> = {
  [ApprovalsTab.Controls]: 'controls',
  [ApprovalsTab.Payments]: 'payments',
};

/** Durandal routes map */
export const durandalRoutes = {
  clients: {
    addNewClient: '#user/tutorials/companyInfo',
    inviteAsClient: '#user/settings/accountingFirm/dialog/companyAccountingFirmInvite',
  },
  user: {
    accounts: {
      index: '#user/bank',
      locked: '#user/bank/locked',
      newBankAccount: '#user/bank/accounts/dialog/newBankAccount',
    },
    dashboard: {
      index: '#user',
      pendingPayments: '#user/dashboard/pendingPayments',
      pendingRequests: '#user/dashboard/pendingRequests',
      paymentApprovals: '#user/dashboard/approvePayments',
      wizard: '#user/dashboard/dashboardWizard',
    },
    onboarding: {
      connectBankAccountDialog: '#user/dialog/addBankAccount',
      firstPayWizard: '#user/your-first-payment',
      firstPayWizardSkipIntro: '#user/your-first-payment/choose-bill',
      outstandingVerificationItems: '#user/dashboard/outstandingVerificationItems',
      validateCompanyDialog: '#user/dashboard/dashboardWizard/dialog/validateCompany',
      verifyIdentity: '#user/dashboard/dashboardWizard/dialog/validateIdentity',
      wizard: '#user/dashboard/dashboardWizard',
    },
    integrations: {
      creditCards: '#user/integrations/creditCardLanding',
    },
    payments: {
      index: '#user/payments',
      locked: '#user/payments/locked',
    },
    referrals: '#user/referrals',
    requests: {
      index: '#user/requests',
      locked: '#user/requests/locked',
    },
    settings: {
      accountingSoftware: '#user/settings/users/integrations',
      controls: '#user/settings/controls',
      profile: '#user/settings',
      billing: '#user/settings/billing',
      billingItem: (billingItemId: string) => `#user/settings/billing/${billingItemId}`,
      sso: '#user/settings/sso',
      userManagement: '#user/settings/users',
      accountingFirmClients: '#user/settings/accountingFirm',
    },
    transferFunds: {
      index: '#user/transferFunds',
      prefundCad: '#user/transferFunds/prefund_PI_CAD',
      prefundUsd: '#user/transferFunds/prefund_PI_USD',
    },
    tutorials: {
      complete: '#user/tutorials/complete',
      companyInfo: '#user/tutorials/companyInfo',
      selectPaymentPlan: '#user/tutorials/selectPaymentPlan',
    },
    capture: '#user/documents',
    payables: '#user/payments',
    paymentAdd: '#user/payments/add',
    paymentEdit: (paymentId: string) => `#user/payments/edit/${paymentId}`,
    paymentDetails: (paymentId: string) => `#user/payments/view/${paymentId}`,
    receivables: '#user/requests',
    receivableEdit: (requestId: string) => `#user/requests/edit/${requestId}`,
    receivableDetails: (requestId: string) => `#user/requests/view/${requestId}`,
    contacts: '#user/payees',
    contactDetails: (payeeId: string) => `#user/payees/view/${payeeId}`,
    contactEdit: (payeeId: string) => `#user/payees/edit/${payeeId}`,
    approvals: {
      default: '#user/approvals',
      tab({
        isRestrictedToMyPayments,
        activeTab,
        itemId,
      }: {
        isRestrictedToMyPayments: boolean;
        activeTab: ApprovalsTab;
        itemId?: string;
      }) {
        const filter = isRestrictedToMyPayments ? ApprovalsFilter.Mine : ApprovalsFilter.All;
        const tab = ApprovalsTabToKey[activeTab];
        return itemId
          ? `#user/approvals/${filter}/${tab}/${itemId}`
          : `#user/approvals/${filter}/${tab}`;
      },
    },
    companySelect: '#user/company/select',
  },
  signIn: '#signin',
  signInWithEmail: (email: string) => `#signin?email=${encodeURIComponent(email)}`,
} as const;
